// src/components/UI/LanguageSwitcher.tsx
// Språkväxlare för SireusCode

import React, { useState } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' }
];

interface LanguageSwitcherProps {
  variant?: 'compact' | 'full' | 'dropdown';
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'compact', 
  className = '' 
}) => {
  const { state, setLanguage } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  
  const currentLanguage = languages.find(lang => lang.code === state.language) || languages[0];

  // Compact variant - simple toggle button
  if (variant === 'compact') {
    return (
      <button
        onClick={() => setLanguage(state.language === 'sv' ? 'en' : 'sv')}
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors rounded-md hover:bg-gray-100 ${className}`}
        aria-label={`Byt till ${state.language === 'sv' ? 'engelska' : 'svenska'}`}
      >
        <Globe className="w-4 h-4" />
        <span className="font-semibold">
          {state.language === 'sv' ? 'EN' : 'SV'}
        </span>
      </button>
    );
  }

  // Full variant - shows current language name
  if (variant === 'full') {
    return (
      <button
        onClick={() => setLanguage(state.language === 'sv' ? 'en' : 'sv')}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors rounded-md hover:bg-gray-100 border border-gray-200 ${className}`}
        aria-label={`Byt språk från ${currentLanguage.nativeName}`}
      >
        <span className="text-lg">{currentLanguage.flag}</span>
        <span>{currentLanguage.nativeName}</span>
        <Globe className="w-4 h-4" />
      </button>
    );
  }

  // Dropdown variant - shows all available languages
  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors rounded-md hover:bg-gray-100"
        aria-label="Välj språk"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="text-lg">{currentLanguage.flag}</span>
        <span className="hidden sm:inline">{currentLanguage.nativeName}</span>
        <span className="sm:hidden font-semibold">{currentLanguage.code.toUpperCase()}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          
          {/* Dropdown menu */}
          <div 
            className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-20"
            role="listbox"
            aria-label="Tillgängliga språk"
          >
            <div className="py-1">
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => {
                    setLanguage(language.code);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm flex items-center gap-3 hover:bg-gray-100 transition-colors ${
                    state.language === language.code 
                      ? 'bg-blue-50 text-blue-600 font-medium' 
                      : 'text-gray-700'
                  }`}
                  role="option"
                  aria-selected={state.language === language.code}
                >
                  <span className="text-lg">{language.flag}</span>
                  <div className="flex flex-col">
                    <span className="font-medium">{language.nativeName}</span>
                    <span className="text-xs text-gray-500">{language.name}</span>
                  </div>
                  {state.language === language.code && (
                    <div className="ml-auto">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSwitcher;